/**
 * Conservative, dependency-free syntax sanity check for code files (edge-safe pure JS).
 * Goal: catch an unambiguously-broken write (unbalanced/mis-nested delimiters) BEFORE it
 * lands, so the model doesn't later waste a turn re-reading + re-editing. Deliberately narrow:
 * it ONLY checks balanced ()/[]/{} after skipping string literals and comments, and ONLY
 * reports when delimiters are clearly wrong. Never throws.
 */

const CODE_RE = /\.(ts|tsx|js|jsx|mjs|cjs)$/;

/** Returns an error message if `content` is clearly broken for a code file, else null. */
export function checkSyntax(path: string, content: string): string | null {
  if (!CODE_RE.test(path)) return null; // only code files
  try {
    const open: Record<string, string> = { ')': '(', ']': '[', '}': '{' };
    const names: Record<string, string> = { '(': "'('", '[': "'['", '{': "'{'" };
    const stack: string[] = [];
    const n = content.length;
    for (let i = 0; i < n; i++) {
      const c = content[i];
      // line comment
      if (c === '/' && content[i + 1] === '/') {
        while (i < n && content[i] !== '\n') i++;
        continue;
      }
      // block comment
      if (c === '/' && content[i + 1] === '*') {
        i += 2;
        while (i < n && !(content[i] === '*' && content[i + 1] === '/')) i++;
        i++; // skip the closing '/' (loop's i++ skips '*')
        continue;
      }
      // string / template literals — skip with escape handling
      if (c === '"' || c === "'" || c === '`') {
        const quote = c;
        i++;
        while (i < n) {
          if (content[i] === '\\') { i += 2; continue; }
          if (content[i] === quote) break;
          i++;
        }
        continue;
      }
      if (c === '(' || c === '[' || c === '{') stack.push(c);
      else if (c === ')' || c === ']' || c === '}') {
        const want = open[c];
        if (stack.length === 0) return `Syntax check failed for ${path}: unexpected closing '${c}'. Fix before writing.`;
        const top = stack.pop()!;
        if (top !== want) return `Syntax check failed for ${path}: mismatched '${c}' (expected to close ${names[top]}). Fix before writing.`;
      }
    }
    if (stack.length > 0) {
      const ch = stack[stack.length - 1];
      return `Syntax check failed for ${path}: unbalanced ${names[ch]} (${stack.length} unclosed). Fix before writing.`;
    }
    return null;
  } catch {
    return null; // never throw — lint is a guard, not a gate that can crash a write
  }
}
